import { useEffect, useRef, useState } from "react";

function useCountUp(end: number, duration: number = 2500) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, end, duration]);

  return { count, ref };
}

const experiences = [
  {
    image:
      "https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/52d75ab299a949da43c65d30392a20c4.png",
    image2:
      "https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/f61c233f398c76d471c5baebda7658e5.png",
    title: "Le Village de Noël",
    url: "https://www.veveynoel.ch/",
    days: "Plus de 40 jours",
    years: 14,
    visitors: 20000,
    visitorLabel: "Plus de 20'000 visiteurs",
  },
  {
    image:
      "https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/949ad8517d517fa23e4fcba495e772e2.png",
    image2:
      "https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/c878b7a791644ef7a362a13550644ea0.png",
    title: "Vevey Riviera Noël",
    url: "https://www.veveyrivieranoel.com/",
    days: "Plus de 35 jours",
    years: 3,
    visitors: 80000,
    visitorLabel: "Plus de 80'000 visiteurs",
  },
  {
    image:
      "https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/84c21ed278c973d3d653be79e8a13370.png",
    image2:
      "https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/0140e3f29521096742987eb6d45dd89a.png",
    title: "Beach Food Festival Vevey",
    url: "https://www.vevey-streatfoodfest.ch/fr/vevey/Accueil/Rendez-vous-au-Vevey-StrEAT-Food-Festival-du-25-au-28-juin-2026.html",
    days: "4 jours",
    years: 7,
    visitors: 15000,
    visitorLabel: "Plus de 15'000 visiteurs",
  },
];

function VisitorCounter({ value }: { value: number }) {
  const { count, ref } = useCountUp(value, 2500);
  const formatted = count.toLocaleString("fr-FR").replace(/\u202f/g, " ");
  return (
    <div
      ref={ref}
      className="text-center py-4 px-4 rounded-xl transition-all duration-300 bg-primary-50/80 border border-primary-200/40"
    >
      <div className="font-playfair text-3xl md:text-4xl font-bold leading-none tracking-tight text-terracotta">
        {formatted}+
      </div>
      <div className="text-[10px] md:text-[11px] tracking-[0.2em] uppercase mt-2 font-light text-[#8B3B3B]/60">
        Visiteurs
      </div>
    </div>
  );
}

export default function ExperiencesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-8");

          cardsRef.current.forEach((card, i) => {
            if (card) {
              setTimeout(() => {
                card.classList.add("opacity-100", "translate-y-0");
                card.classList.remove("opacity-0", "translate-y-6");
              }, 150 * i);
            }
          });
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="experiences"
      ref={sectionRef}
      className="py-16 md:py-24 relative opacity-0 translate-y-8 transition-all duration-700 bg-gradient-coral"
    >
      {/* Subtle depth overlay for the gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10 pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Title */}
        <h2 className="text-center font-playfair text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-white mb-6 md:mb-8 tracking-tight">
          NOS EXPÉRIENCES SIGNATURE
        </h2>

        {/* Intro */}
        <div className="text-center max-w-[54rem] mx-auto mb-10 md:mb-14">
          <p className="text-base sm:text-lg md:text-xl text-white/70 leading-[1.8]">
            Une approche pensée pour allier{" "}
            <strong className="text-white font-medium">créativité</strong>,{" "}
            <strong className="text-white font-medium">structure</strong>,{" "}
            <strong className="text-white font-medium">fluidité opérationnelle</strong>{" "}
            et{" "}
            <strong className="text-white font-medium">expérience publique</strong>
            , afin de concevoir des événements cohérents, marquants et
            parfaitement orchestrés.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {experiences.map((exp, idx) => (
            <a
              key={exp.title}
              href={exp.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block no-underline"
            >
              <div
                ref={(node) => { cardsRef.current[idx] = node; }}
                className="group relative bg-background-50/90 backdrop-blur-[12px] border border-white/10 rounded-2xl overflow-hidden opacity-0 translate-y-6 transition-all duration-700 ease-out hover:bg-background-50 hover:border-white/20 hover:shadow-[0_20px_60px_rgba(0,0,0,0.15)] hover:-translate-y-2"
                style={{ willChange: "transform" }}
              >
                {/* Soft coral glow on hover */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    background: "radial-gradient(ellipse at 50% 0%, rgba(139,59,59,0.06) 0%, transparent 60%)",
                  }}
                />

                {/* Vertical image stack */}
                <div className="relative z-10 p-2 flex flex-col gap-1.5">
                  <div className="relative w-full aspect-[3/2] rounded-lg overflow-hidden">
                    <img
                      src={exp.image}
                      alt={`${exp.title} — vue principale`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="relative w-full aspect-[3/2] rounded-lg overflow-hidden">
                    <img
                      src={exp.image2}
                      alt={`${exp.title} — ambiance`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="relative z-10 p-6 md:p-7">
                  <h3 className="font-sans text-[1.15rem] md:text-[1.25rem] font-semibold mb-4 leading-tight tracking-tight text-terracotta">
                    {exp.title}
                  </h3>

                  <div className="w-8 h-px bg-primary-200/50 mb-4" />

                  <div className="space-y-2.5 mb-5">
                    <p className="text-[13px] md:text-sm text-foreground-800 leading-[1.75]">
                      <strong className="text-foreground-900 font-semibold">{exp.days}</strong>{" "}
                      d&apos;exploitation
                    </p>
                    <p className="text-[13px] md:text-sm text-foreground-800 leading-[1.75]">
                      <strong className="text-foreground-900 font-semibold">Création et gestion de A à Z</strong>{" "}
                      depuis{" "}
                      <strong className="text-foreground-900 font-semibold">{exp.years} ans</strong>
                    </p>
                    <p className="text-[13px] md:text-sm text-foreground-500 italic leading-[1.75]">
                      {exp.visitorLabel}
                    </p>
                  </div>

                  <VisitorCounter value={exp.visitors} />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}